import { useState } from 'react';

import { useSearchParams } from '@remix-run/react';

import type { ModalType } from '~/interfaces/modal_type';
import { NECTAR_PAGE_BLOCK_GET_PARAM_NAME } from '~/utils/nectarTrackingFields';

// Types RelativeRoutingType and NavigationOptions are copied over from
// react-router package
type RelativeRoutingType = 'route' | 'path';

type NavigationOptions = {
  replace?: boolean;
  state?: unknown;
  preventScrollReset?: boolean;
  relative?: RelativeRoutingType;
  flushSync?: boolean;
  viewTransition?: boolean;
};

interface ReturnProps {
  currentModal: ModalType | null;
  isModalOpen: boolean;
  handleCloseModal: (options?: NavigationOptions, additionalParams?: string[]) => void;
  handleOpenModal: (modal: ModalType, options?: NavigationOptions, additionalParams?: Record<string, string>) => void;
}

const VALID_MODAL_TYPES: ModalType[] = [
  'search',
  'community_join',
  'community_login',
  'community_channel_upgrade',
  'community_content_upgrade',
  'community_answer_question',
  'login',
  'signup',
  'recommendations',
  'edit_profile',
  'reset_password',
  'unsubscribe',
  'resubscribe',
  'change_plan',
  'unpause_plan',
  'downgrade',
  'unpause',
  'connected_accounts',
  'create_product_review',
];

const ALLOWED_ADDITIONAL_PARAMS = [
  'after_login_action_post_id',
  'after_login_action',
  'redirect_to',
  'destination',
  'review_order_id',
  'review_product_slug',
  'upgrade_return',
  'space_id',
  'resource_id',
  NECTAR_PAGE_BLOCK_GET_PARAM_NAME,
];

const useGlobalModals = ({ useLocalState }: { useLocalState: boolean } = { useLocalState: false }): ReturnProps => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localModalType, setLocalModalParam] = useState<ModalType | null>(null);

  const handleCloseModal = (options?: NavigationOptions) => {
    if (useLocalState) {
      setLocalModalParam(null);
      return;
    }

    searchParams.delete('modal');
    ALLOWED_ADDITIONAL_PARAMS.forEach((param) => {
      searchParams.delete(param);
    });
    setSearchParams(searchParams, { replace: true, ...options });
  };

  const handleOpenModal = (
    modal: ModalType,
    options?: NavigationOptions,
    additionalParams?: Record<string, string>,
  ) => {
    if (useLocalState) {
      setLocalModalParam(modal);
      return;
    }

    searchParams.set('modal', modal);
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (!ALLOWED_ADDITIONAL_PARAMS.includes(key)) return;
        searchParams.set(key, value);
      });
    }

    setSearchParams(searchParams, { replace: true, ...options });
  };

  const currentModal = useLocalState ? localModalType : (searchParams.get('modal') as ModalType);

  return {
    currentModal,
    isModalOpen: currentModal ? VALID_MODAL_TYPES.includes(currentModal) : false,
    handleCloseModal,
    handleOpenModal,
  };
};

export default useGlobalModals;
